import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { soinsService } from '../services/soins';
import type { CreateSoinInput, UpdateSoinInput } from '../types/entities';
import { toast } from 'sonner';
import { apiClient } from '../services/api';

export function useSoins() {
  return useQuery({
    queryKey: ['soins'],
    queryFn: () => soinsService.getAll(),
  });
}

export function useSoin(id: string) {
  return useQuery({
    queryKey: ['soins', id],
    queryFn: () => soinsService.getById(id),
    enabled: !!id,
  });
}

export function useCreateSoin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSoinInput) => soinsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['soins'] });
      toast.success('Soin créé avec succès');
    },
    onError: (error) => {
      toast.error(apiClient.getErrorMessage(error));
    },
  });
}

export function useUpdateSoin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSoinInput }) =>
      soinsService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['soins'] });
      queryClient.invalidateQueries({ queryKey: ['soins', variables.id] });
      toast.success('Soin mis à jour');
    },
    onError: (error) => {
      toast.error(apiClient.getErrorMessage(error));
    },
  });
}

export function useDeleteSoin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => soinsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['soins'] });
      toast.success('Soin supprimé');
    },
    onError: (error) => {
      toast.error(apiClient.getErrorMessage(error));
    },
  });
}
